import BusinessIcon from '@mui/icons-material/Business'
import {
    Box,
    Button,
    Chip,
    List,
    ListItem,
    ListItemText,
    Stack,
    Typography
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { DateTime } from 'luxon'
import PropTypes from 'prop-types'
import { Link as RouterLink } from 'react-router-dom'
import * as endPoints from '@/endpoints'
import { partnerStatus } from '@/features/dashboard/site-admin/partnerStatus'
import axios from '@/lib/axios'
import EntityLink from '../EntityLink'
import PeekLayout, {
    PeekDetails,
    PeekPlaceholder,
    PeekSection
} from './PeekLayout'

const seats = (used, max) => `${used ?? 0} of ${max ?? 'unlimited'}`

function Swatch({ color }) {
    if (!color) return '—'
    return (
        <Stack direction="row" spacing={1} alignItems="center" component="span">
            <Box
                component="span"
                sx={{
                    width: 14,
                    height: 14,
                    borderRadius: 0.5,
                    bgcolor: color,
                    border: 1,
                    borderColor: 'divider',
                    display: 'inline-block'
                }}
            />
            <span>{color}</span>
        </Stack>
    )
}

Swatch.propTypes = { color: PropTypes.string }

/**
 * A reseller / white-label partner, for site admins: its licence, how much of
 * it is used, how it's branded, and the organisations it manages.
 */
export default function PartnerPeek({ id }) {
    const query = useQuery({
        queryKey: ['siteAdmin', 'partner', id],
        queryFn: () =>
            axios
                .get(`${endPoints.ADMIN_PARTNERS}${id}/`)
                .then((response) => response.data),
        retry: false
    })
    const partner = query.data

    if (!partner) {
        return <PeekPlaceholder kind="Partner" loading={query.isLoading} />
    }

    const orgs = partner.orgs ?? []
    const entitlements = Object.entries(partner.feature_entitlements ?? {})
        .filter(([, on]) => on)
        .map(([key]) => key)
        .sort()

    return (
        <PeekLayout
            kind="Partner"
            title={partner.name}
            subtitle={[partner.short_name, partner.product_name]
                .filter(Boolean)
                .join(' · ')}
            actions={
                <Button
                    component={RouterLink}
                    to="/site-admin/partners"
                    size="small"
                    variant="outlined"
                    startIcon={<BusinessIcon fontSize="small" />}
                >
                    Manage partners
                </Button>
            }
        >
            <PeekSection title="Licence">
                <PeekDetails
                    rows={[
                        {
                            label: 'Status',
                            value: (
                                <Chip
                                    size="small"
                                    color={partnerStatus(partner).color}
                                    label={partnerStatus(partner).label}
                                />
                            )
                        },
                        {
                            label: 'Organisations',
                            value: seats(partner.org_count, partner.max_orgs)
                        },
                        {
                            label: 'Users',
                            value: seats(partner.user_count, partner.max_users)
                        },
                        {
                            label: 'Expires',
                            value: partner.license_expires_at
                                ? DateTime.fromISO(
                                      partner.license_expires_at
                                  ).toLocaleString(DateTime.DATE_MED)
                                : 'Never'
                        },
                        {
                            label: 'Features',
                            value: entitlements.length
                                ? entitlements.join(', ')
                                : 'No restriction'
                        }
                    ]}
                />
            </PeekSection>

            <PeekSection title="Branding">
                <PeekDetails
                    rows={[
                        {
                            label: 'Product name',
                            value: partner.product_name || '—'
                        },
                        { label: 'Subdomain', value: partner.slug || '—' },
                        {
                            label: 'Support email',
                            value: partner.support_email || '—'
                        },
                        {
                            label: 'Primary colour',
                            value: <Swatch color={partner.primary_color} />
                        },
                        {
                            label: 'Secondary colour',
                            value: <Swatch color={partner.secondary_color} />
                        }
                    ]}
                />
            </PeekSection>

            <PeekSection title={`Organisations (${orgs.length})`}>
                {orgs.length > 0 ? (
                    <List dense disablePadding>
                        {orgs.map((org) => (
                            <ListItem key={org.id} disableGutters>
                                <ListItemText
                                    primary={
                                        <EntityLink
                                            type="organisation"
                                            id={org.id}
                                        >
                                            {org.name}
                                        </EntityLink>
                                    }
                                    secondary={org.short_name || null}
                                />
                            </ListItem>
                        ))}
                    </List>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        This partner doesn’t manage any organisations yet.
                    </Typography>
                )}
            </PeekSection>
        </PeekLayout>
    )
}

PartnerPeek.propTypes = {
    id: PropTypes.string.isRequired
}
